import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Mail,
  Power,
  Search,
  Send,
  ShieldAlert,
  Trash2,
  Users,
} from 'lucide-react';
import { format } from 'date-fns';
import Modal from '../components/ui/Modal';
import Confirm from '../components/ui/Confirm';
import EliminarAlumnoModal from '../components/EliminarAlumnoModal';
import {
  borrarFilaGuardianSinVinculos,
  familyShapes,
  inviteTutor,
  listFamilies,
  reenviarAccesoTutor,
  setCuentaTutorActiva,
  tutorAccess,
  tutoresInvisibles,
  type Family,
  type FamilyShape,
  type FamilyShapeRow,
  type TutorAccess,
  type TutorInvisible,
} from '../services/guardians';

// ============================================================
// FAMILIAS — quién puede ver los datos de cada niño
// ============================================================
// El modelo de tutor (migs 196-209) está entero y vivo, pero hasta aquí no
// tenía dónde mirarse. Dirección podía vincular un tutor desde la ficha de UN
// alumno y no tenía forma de contestar las dos preguntas que de verdad importan:
//
//   · ¿Qué familias siguen en la forma vieja, y qué falta para cada una?
//   · ¿Quién tiene —y quién ha tenido— acceso a los datos de un menor?
//
// La página son esas dos preguntas, en ese orden.
//
// ⚠️ LOS CONTADORES SALEN DE `admin_family_shapes()`, que envuelve la MISMA
//    `family_shapes()` que usa la conversión (mig 208). No se cuenta aquí por
//    nuestra cuenta a propósito: si el panel tuviera su propia idea de qué es
//    una familia pendiente, diría "quedan 3" mientras la conversión piensa otra
//    cosa, y la que manda es la de SQL.
//
// ============================================================
// POR QUÉ HAY CASOS SIN BOTÓN
// ============================================================
// De las cuatro formas pendientes, dirección solo puede cerrar dos desde aquí.
// `cuenta_es_del_hijo` exige al padre delante tecleando su contraseña —es lo que
// hace que la familia no se quede ni un instante sin poder entrar (mig 208)— y
// `conflicto` no lo resuelve el modelo por sí solo.
//
// Se listan igualmente, con sus datos de contacto, porque en algunas de ellas el
// trabajo de dirección es llamar por teléfono. Una pantalla que solo enseñara lo
// accionable escondería justo los casos que necesitan una persona. Cuál de ellas
// se resuelve llamando lo dice la propia fila: ver el bloque siguiente.
//
// ============================================================
// `conflicto` SON DOS COSAS DISTINTAS, Y SE DISTINGUEN POR `blocker_role`
// ============================================================
// `family_shapes` manda a `conflicto` todo perfil cuyo `parent_email` lo ocupa
// alguien que no es ni el propio alumno ni un tutor. Eso mete en el mismo cajón
// dos situaciones que no se parecen en nada:
//
//   · `blocker_role = 'alumno'` — el email es el login de OTRO ALUMNO. En la
//     práctica es un hermano dado de alta antes: el padre no pudo repetir su
//     correo (`auth.users.email` es único) y usó un alias con `+`. No hay
//     ningún conflicto de rol. Se destraba convirtiendo la cuenta del hermano,
//     no llamando por teléfono.
//
//   · cualquier otro rol — un monitor, un director o un adulto usa ese buzón.
//     Ese sí es el conflicto que el modelo de un rol por perfil no resuelve.
//
// Hasta aquí la celda pintaba el segundo texto para los dos, así que al único
// caso que hay hoy en producción —dos hermanos— le decía a dirección que el
// email lo usaba un monitor. Le decía que llamase por un problema que no
// existe y le escondía el que sí.
//
// ⚠️ AL HERMANO SE LE PONE NOMBRE SIN PEDIR NADA A SQL. `family_shapes` devuelve
//    el rol del ocupante pero no su nombre; y el ocupante, si es alumno, está
//    YA en esta misma lista —es la fila cuyo `student_email` es este
//    `parent_email`—. Se busca ahí. Si no aparece (un monitor no declara tutor,
//    así que `family_shapes` no lo devuelve nunca) se dice "otro alumno" sin
//    nombre, que sigue siendo verdad. Ni una lectura más.

/** Las cuatro formas que piden trabajo. `ya_vinculado` es la quinta y es el final feliz. */
type PendingShape = Exclude<FamilyShape, 'ya_vinculado'>;
type PendingRow = FamilyShapeRow & { shape: PendingShape };

const isPending = (r: FamilyShapeRow): r is PendingRow => r.shape !== 'ya_vinculado';

const SHAPE_META: Record<PendingShape, { label: string; help: string; tone: string }> = {
  cuenta_es_del_hijo: {
    label: 'Cuenta a nombre del hijo',
    // ⚠️ La frase que había aquí —"lo convierte el propio padre desde la app"—
    //    describe el diseño de la mig 208, no lo que existe. `convert_own_
    //    account_to_family` está viva en la base y con GRANT a `authenticated`,
    //    pero NINGUNA pantalla la llama: el grep en `app/src` y `admin/src` solo
    //    la encuentra en `database.types.ts`. Dirección estaba leyendo una
    //    instrucción que, si la seguía, mandaba a la familia a buscar una
    //    pantalla que no existe.
    help: 'El login del alumno es el email del padre. Un segundo hermano NO puede registrarse: auth.users.email es único. Todavía no hay camino para convertirla: la conversión existe en la base de datos pero ninguna pantalla la ofrece.',
    tone: 'text-error',
  },
  tutor_sin_cuenta: {
    label: 'Tutor sin cuenta',
    help: 'El hijo tiene su propio buzón y el del tutor está libre. Se le crea la cuenta y se le manda el enlace para definir contraseña. El hijo no se toca.',
    tone: 'text-warning',
  },
  solo_vincular: {
    label: 'Falta el vínculo',
    help: 'El tutor ya tiene cuenta —se registró para un hermano, por ejemplo— y solo falta vincularlo a este alumno. Se hace desde la ficha del alumno.',
    tone: 'text-warning',
  },
  conflicto: {
    // Esta etiqueta la comparten el contador —que suma los dos casos de
    // `conflicto`— y la fila cuando no se puede afinar más. Tiene que ser
    // cierta para un hermano Y para un monitor, así que nombra el hecho
    // (el email está ocupado) y no la causa.
    label: 'Email ocupado por otra cuenta',
    help: 'El email del tutor lo usa ya otra cuenta que no es ni la del alumno ni la de un tutor.',
    tone: 'text-error',
  },
};

/**
 * Qué pintar en la fila de un `conflicto`. Ver la cabecera: son dos situaciones
 * distintas metidas en la misma forma, y la única manera de distinguirlas es
 * `blocker_role`.
 *
 * `bloqueante` sale de la propia lista, no de una lectura nueva: el ocupante del
 * email, cuando es alumno, es la fila cuyo login es este `parent_email`.
 */
function detalleConflicto(
  row: FamilyShapeRow,
  todas: FamilyShapeRow[]
): { label: string; help: string; accion: string } {
  if (row.blocker_role !== 'alumno') {
    return {
      label: 'Conflicto de rol',
      help: 'Ese email lo usa un monitor, un director o un adulto. El modelo tiene un rol por perfil: un entrenador que además es padre no cabe en las dos cosas a la vez.',
      accion: 'Requiere contacto',
    };
  }

  // Los dos lados normalizados igual que en `family_shapes` (mig 208): un padre
  // teclea `Juan@Gmail.com` en el formulario y GoTrue guarda minúsculas.
  const norm = (e: string | null) => (e ?? '').trim().toLowerCase();
  const objetivo = norm(row.parent_email);
  const hermano = objetivo
    ? todas.find((r) => r.student_id !== row.student_id && norm(r.student_email) === objetivo)
    : undefined;

  return {
    label: 'El email lo usa otro alumno',
    help: hermano
      ? `Ese email es el login de ${hermano.student_name}, que se dio de alta antes con el correo del padre. Por eso este alumno lleva un alias con "+": auth.users.email es único y el padre no pudo repetirlo. No es un conflicto de rol y no se arregla llamando: se destraba al convertir la cuenta de ${hermano.student_name}, y entonces este alumno pasa a "Falta el vínculo".`
      : 'Ese email es el login de otro alumno del club, casi siempre un hermano dado de alta antes con el correo del padre. Por eso este alumno lleva un alias con "+": auth.users.email es único y el padre no pudo repetirlo. No es un conflicto de rol: se destraba al convertir la cuenta de ese alumno.',
    accion: 'Depende de otra cuenta',
  };
}

const ORDER: PendingShape[] = [
  'cuenta_es_del_hijo',
  'tutor_sin_cuenta',
  'solo_vincular',
  'conflicto',
];

const RELATIONSHIPS = [
  { value: '', label: 'Sin especificar' },
  { value: 'madre', label: 'Madre' },
  { value: 'padre', label: 'Padre' },
  { value: 'tutor_legal', label: 'Tutor legal' },
  { value: 'otro', label: 'Otro' },
];

// ============================================================
// EL ESTADO DE ACCESO DE UN TUTOR
// ============================================================
// Traducción de los tres estados de /directores (mig 182) a Familias, con dos
// más que allí no existen porque allí no hay conversión de por medio.
//
// El orden importa: se comprueba primero lo que IMPIDE entrar y después lo que
// solo cuenta si ya podía. Un tutor sin confirmar no «nunca ha entrado»: es
// que GoTrue no le deja, y eso se arregla de otra manera.
type EstadoAcceso = 'revocada' | 'sin_confirmar' | 'sin_contrasena' | 'nunca' | 'entra';

function estadoDe(f: Family, a: TutorAccess | undefined): EstadoAcceso | null {
  // Sin cuenta no hay nada que medir: un tutor declarativo es una ficha.
  if (!f.hasAccount || !a) return null;
  if (f.accountActive === false) return 'revocada';
  if (!a.email_confirmado) return 'sin_confirmar';
  if (!a.tiene_contrasena) return 'sin_contrasena';
  if (!a.ultimo_acceso) return 'nunca';
  return 'entra';
}

const ESTADO_META: Record<
  Exclude<EstadoAcceso, 'entra' | 'revocada'>,
  { label: string; clase: string; title: string }
> = {
  sin_confirmar: {
    label: 'correo sin confirmar',
    clase: 'badge-error',
    title: 'No puede entrar: sin confirmar el correo, GoTrue le niega el login',
  },
  sin_contrasena: {
    label: 'pendiente de estrenar',
    clase: 'badge-warning',
    title: 'La cuenta existe y el correo está confirmado, pero aún no tiene contraseña',
  },
  nunca: {
    label: 'nunca ha entrado',
    clase: 'badge-error',
    title: 'Puede entrar y no lo ha hecho todavía, ni él ni la cuenta de la que salió',
  },
};

/** «hace 3 días». Sin dependencias nuevas: es una resta. */
function hace(iso: string): string {
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  return `hace ${dias} días`;
}

export default function FamiliasPage() {
  const [shapes, setShapes] = useState<FamilyShapeRow[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState<PendingShape | null>(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [inviting, setInviting] = useState<FamilyShapeRow | null>(null);
  const [relationship, setRelationship] = useState('');
  const [busy, setBusy] = useState(false);
  // BUG 14 · la cuenta del tutor: desactivar/activar y eliminar.
  const [invisibles, setInvisibles] = useState<TutorInvisible[]>([]);
  const [confirmActiva, setConfirmActiva] = useState<Family | null>(null);
  const [eliminando, setEliminando] = useState<Family | null>(null);
  // Mig 301 · si ese tutor ha llegado a entrar, y cuándo.
  const [accesos, setAccesos] = useState<Map<string, TutorAccess>>(new Map());
  const [accesoError, setAccesoError] = useState<string | null>(null);
  const [reenviando, setReenviando] = useState<Family | null>(null);
  const [enviando, setEnviando] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, f, acc] = await Promise.all([familyShapes(), listFamilies(), tutorAccess()]);
    setShapes(s);
    setAccesos(acc.accesos);
    setAccesoError(acc.error);
    // Mismo criterio que `tutoresInvisibles` aquí abajo, que ya lo hacía bien:
    // el censo de familias responde «quién ha tenido acceso a este niño», y una
    // lista vacía por un fallo de lectura contesta «nadie». Se dice que no se
    // pudo leer y no se pinta la tabla.
    if (f.error) {
      setFamilies([]);
      setError(`No se ha podido cargar el censo de familias: ${f.error}`);
      setLoading(false);
      return;
    }
    setFamilies(f.familias);
    // `tutoresInvisibles` PROPAGA su error a propósito: una lista vacía por un
    // fallo de lectura diría "no hay ninguna cuenta suelta", que es la
    // conclusión contraria a la verdad. Aquí se dice que no se pudo comprobar.
    try {
      setInvisibles(await tutoresInvisibles());
    } catch (e) {
      setInvisibles([]);
      setError(
        `No se pudo comprobar si hay cuentas de tutor fuera de esta lista: ${
          e instanceof Error ? e.message : 'error desconocido'
        }`
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => {
    const out: Record<PendingShape, number> = {
      cuenta_es_del_hijo: 0,
      tutor_sin_cuenta: 0,
      solo_vincular: 0,
      conflicto: 0,
    };
    for (const row of shapes.filter(isPending)) out[row.shape] += 1;
    return out;
  }, [shapes]);

  const pending = useMemo(() => {
    const rows = shapes.filter(isPending);
    return filter ? rows.filter((r) => r.shape === filter) : rows;
  }, [shapes, filter]);

  const familiasFiltradas = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return families;
    return families.filter(
      (f) =>
        f.full_name.toLowerCase().includes(q) ||
        (f.email ?? '').toLowerCase().includes(q) ||
        f.links.some((l) => l.student_name.toLowerCase().includes(q)) ||
        f.revoked.some((l) => l.student_name.toLowerCase().includes(q))
    );
  }, [families, search]);

  // BUG 14 · desactivar es el paso 1 de 2. `admin_delete_account` responde
  // `22023 · Hay que desactivar la cuenta antes de poder eliminarla`, así que
  // un botón de borrar sin esto sería el mismo bug en un sitio nuevo.
  // El porqué de que esto NO pase por una edge function, como sí hace el
  // «Reinvitar» de /directores, está en `reenviarAccesoTutor`.
  async function reenviarAcceso(f: Family) {
    if (!f.email) return;
    setEnviando(true);
    setError('');
    setSuccess('');
    const r = await reenviarAccesoTutor(f.email, `${window.location.origin}/set-password`);
    setEnviando(false);
    setReenviando(null);
    if (!r.ok) {
      setError(
        `No se ha podido enviar el correo a ${f.email}: ${r.message ?? 'error desconocido'}`
      );
      return;
    }
    setSuccess(`Enviado a ${f.email}. El enlace le deja definir su contraseña y entrar.`);
  }

  async function toggleActiva(f: Family) {
    if (!f.profile_id) return;
    setBusy(true);
    setError('');
    setSuccess('');
    const r = await setCuentaTutorActiva(f.profile_id, !f.accountActive);
    setBusy(false);
    if (!r.ok) {
      setError(r.message ?? 'No se pudo cambiar el estado de la cuenta.');
      return;
    }
    setSuccess(
      f.accountActive
        ? `Cuenta de ${f.full_name} desactivada. Ya no puede entrar y se le han cerrado las sesiones.`
        : `Cuenta de ${f.full_name} reactivada.`
    );
    await load();
  }

  // Se llama DESPUÉS de que la RPC haya borrado la cuenta. Ese orden es
  // deliberado: si esta segunda escritura falla, queda una ficha de tutor sin
  // cuenta —el comportamiento de siempre— y se dice. Al revés se habría
  // destruido la ficha de una cuenta que sigue viva.
  async function tutorBorrado(f: Family) {
    const vinculos = f.links.length + f.revoked.length;
    let cola: string;
    if (vinculos === 0) {
      const r = await borrarFilaGuardianSinVinculos(f.guardian_id);
      cola = r.ok
        ? ' Su ficha de tutor también: no le quedaba ningún vínculo.'
        : ` La cuenta sí, pero su ficha de tutor no: ${r.message ?? 'error desconocido'}`;
    } else {
      cola =
        ' Su ficha de tutor se conserva sin cuenta, con el historial de accesos. Esa familia vuelve a los pendientes como «Tutor sin cuenta».';
    }
    setEliminando(null);
    setError('');
    setSuccess(`Cuenta de ${f.full_name} eliminada.${cola}`);
    await load();
  }

  async function handleInvite() {
    if (!inviting) return;
    setBusy(true);
    setError('');
    setSuccess('');
    const res = await inviteTutor(inviting.student_id, relationship || null);
    setBusy(false);
    if (!res.ok) {
      setError(res.message ?? 'No se pudo invitar al tutor.');
      return;
    }
    setSuccess(
      res.message ??
        `${inviting.parent_name} ya tiene acceso a los datos de ${inviting.student_name}. Le hemos mandado el enlace para definir su contraseña.`
    );
    setInviting(null);
    setRelationship('');
    await load();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Users className="text-gold-primary" size={24} />
        <div>
          <h2 className="font-oswald text-2xl uppercase tracking-wide text-white">Familias</h2>
          <p className="text-text-secondary text-sm font-inter">
            Quién puede ver los datos de cada alumno, y qué familias siguen sin tutor propio. Las
            familias que se dieron de baja no salen aquí: su rastro está en el registro de
            auditoría, y sin nombres.
          </p>
        </div>
      </div>

      {error && (
        <div
          className="flex items-center gap-2 text-error text-sm font-inter"
          data-testid="fam-error"
        >
          <AlertCircle size={16} /> {error}
        </div>
      )}
      {/* BUG 14 · esta lista sale de `guardians`, así que una cuenta con
          `role = 'tutor'` y sin fila ahí NO se pinta. Hoy no hay ninguna, pero
          si un alta futura deja una suelta, ser invisible es exactamente cómo
          una cuenta que dirección creía borrada siguió entrando seis días. Se
          sacan por su nombre en vez de callarlas. */}
      {invisibles.length > 0 && (
        <div
          className="rounded-lg border border-error/40 bg-error/10 p-3 text-sm font-inter"
          data-testid="tutores-invisibles"
        >
          <p className="flex items-center gap-2 text-error">
            <AlertCircle size={16} />
            {invisibles.length} cuenta{invisibles.length === 1 ? '' : 's'} de tutor sin ficha de
            familia: no aparece{invisibles.length === 1 ? '' : 'n'} en la lista de abajo.
          </p>
          <ul className="mt-2 space-y-1 text-text-primary">
            {invisibles.map((t) => (
              <li key={t.id} data-testid={`invisible-${t.id}`}>
                {t.full_name} · {t.email ?? 'sin email'}
                {!t.active && <span className="badge-error text-xs ml-2">inactiva</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {success && (
        <div
          className="flex items-center gap-2 text-success text-sm font-inter"
          data-testid="fam-success"
        >
          <CheckCircle size={16} /> {success}
        </div>
      )}

      {/* ---------- Contadores ---------- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {ORDER.map((shape) => (
          <button
            key={shape}
            type="button"
            data-testid={`contador-${shape}`}
            onClick={() => setFilter((f) => (f === shape ? null : shape))}
            className={`card text-left transition-colors ${
              filter === shape ? 'border-gold-primary' : 'hover:border-bg-border'
            }`}
            aria-pressed={filter === shape}
          >
            <p className={`font-oswald text-3xl ${SHAPE_META[shape].tone}`}>{counts[shape]}</p>
            <p className="text-text-secondary text-xs font-inter mt-1">{SHAPE_META[shape].label}</p>
          </button>
        ))}
      </div>

      {/* ---------- Pendientes ---------- */}
      <div className="card p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-bg-border">
          <h3 className="text-text-secondary text-xs uppercase tracking-wider">
            Familias por resolver
          </h3>
          {filter && (
            <button className="btn-ghost text-xs" onClick={() => setFilter(null)}>
              Ver todas
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-text-secondary text-sm font-inter p-4">Cargando…</p>
        ) : pending.length === 0 ? (
          <p className="text-text-secondary text-sm font-inter p-4" data-testid="pendientes-vacio">
            {filter
              ? 'Ninguna familia en esa situación.'
              : 'Todas las familias con tutor declarado están resueltas.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-inter">
              <thead className="border-b border-bg-border bg-bg-elevated">
                <tr>
                  <th className="table-th">Alumno</th>
                  <th className="table-th">Tutor declarado</th>
                  <th className="table-th">Qué pasa</th>
                  <th className="table-th text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((row) => {
                  // `conflicto` son dos situaciones; el resto se describe solo.
                  const meta =
                    row.shape === 'conflicto'
                      ? detalleConflicto(row, shapes)
                      : { ...SHAPE_META[row.shape], accion: 'Requiere contacto' };
                  return (
                    <tr
                      key={row.student_id}
                      className="table-row"
                      data-testid={`pend-${row.shape}`}
                    >
                      <td className="table-td">
                        <Link
                          to={`/alumnos/${row.student_id}`}
                          className="font-medium hover:text-gold-primary"
                        >
                          {row.student_name}
                        </Link>
                        <p className="text-text-secondary text-xs">{row.student_email ?? '—'}</p>
                      </td>
                      <td className="table-td">
                        <p>{row.parent_name ?? '—'}</p>
                        {/* El teléfono se pinta porque en dos de las cuatro formas
                          el trabajo de dirección es llamar, no pulsar. */}
                        <p className="text-text-secondary text-xs">
                          {row.parent_email ?? '—'}
                          {row.parent_phone ? ` · ${row.parent_phone}` : ''}
                        </p>
                      </td>
                      <td className="table-td max-w-md" data-testid={`forma-${row.student_id}`}>
                        <span className={`text-xs font-medium ${SHAPE_META[row.shape].tone}`}>
                          {meta.label}
                        </span>
                        {/* El rol del ocupante, en crudo. No se pinta cuando es
                          'alumno' porque la etiqueta ya lo dice: sería
                          "El email lo usa otro alumno · alumno". */}
                        {row.shape === 'conflicto' &&
                          row.blocker_role &&
                          row.blocker_role !== 'alumno' && (
                            <span className="badge-warning text-xs ml-2">{row.blocker_role}</span>
                          )}
                        <p className="text-text-secondary text-xs mt-1">{meta.help}</p>
                      </td>
                      <td className="table-td text-right" data-testid={`accion-${row.student_id}`}>
                        {row.shape === 'tutor_sin_cuenta' ? (
                          <button
                            className="btn-gold text-xs"
                            data-testid={`invitar-${row.student_id}`}
                            onClick={() => {
                              setInviting(row);
                              setRelationship('');
                            }}
                          >
                            <Mail size={14} /> Invitar al tutor
                          </button>
                        ) : row.shape === 'solo_vincular' ? (
                          <Link to={`/alumnos/${row.student_id}`} className="btn-ghost text-xs">
                            Ir a la ficha
                          </Link>
                        ) : (
                          <span className="text-text-secondary text-xs">{meta.accion}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---------- Censo de familias ---------- */}
      <div className="card p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-bg-border gap-4">
          <h3 className="text-text-secondary text-xs uppercase tracking-wider">
            Familias vinculadas
          </h3>
          <div className="relative w-64">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              className="input pl-8 h-8 text-xs"
              placeholder="Tutor, email o alumno…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="buscar-familia"
            />
          </div>
        </div>

        {loading ? (
          <p className="text-text-secondary text-sm font-inter p-4">Cargando…</p>
        ) : familiasFiltradas.length === 0 ? (
          <p className="text-text-secondary text-sm font-inter p-4" data-testid="familias-vacio">
            No hay tutores que encajen.
          </p>
        ) : (
          <ul className="divide-y divide-bg-border">
            {familiasFiltradas.map((f) => (
              <li key={f.guardian_id} className="p-4" data-testid={`familia-${f.guardian_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-white">
                      {f.full_name}
                      {!f.hasAccount && (
                        <span
                          className="badge-warning text-xs ml-2"
                          title="No puede entrar en la app"
                        >
                          sin cuenta
                        </span>
                      )}
                      {f.hasAccount && f.accountActive === false && (
                        <span
                          className="badge-error text-xs ml-2"
                          data-testid={`inactiva-${f.guardian_id}`}
                          title="Desactivada: no puede entrar"
                        >
                          inactiva
                        </span>
                      )}
                    </p>
                    <p className="text-text-secondary text-xs">
                      {f.email ?? '—'}
                      {f.phone ? ` · ${f.phone}` : ''}
                    </p>

                    {/* ---------- Si ha llegado a entrar, y cuándo (mig 301) ----------
                        Sin esto, una familia podía estar DOCE DÍAS sin poder
                        entrar y nadie se enteraba: no había dónde mirarlo. */}
                    {f.hasAccount &&
                      (() => {
                        // Un fallo de lectura NO se pinta como «nunca ha
                        // entrado»: eso es una afirmación, y la contraria a la
                        // verdad en 32 de 33 familias.
                        if (accesoError) {
                          return (
                            <p
                              className="text-error text-xs mt-1"
                              title={`No se ha podido leer el estado de acceso: ${accesoError}`}
                              data-testid={`acceso-ilegible-${f.guardian_id}`}
                            >
                              No se ha podido comprobar si ha entrado
                            </p>
                          );
                        }
                        const a = accesos.get(f.profile_id ?? '');
                        if (!a) return null;
                        const estado = estadoDe(f, a);
                        const meta =
                          estado && estado !== 'entra' && estado !== 'revocada'
                            ? ESTADO_META[estado]
                            : null;
                        // La cuenta la creó una migración si nació después de
                        // que la familia ya estuviera en el club. Se dice, para
                        // que un «alta en agosto» junto a una cuenta de
                        // septiembre no parezca un error de la pantalla.
                        const migrada =
                          a.alta_familia !== null &&
                          new Date(a.cuenta_creada).getTime() - new Date(a.alta_familia).getTime() >
                            86_400_000;
                        return (
                          <p
                            className="text-text-secondary text-xs mt-1"
                            data-testid={`acceso-${f.guardian_id}`}
                          >
                            {a.alta_familia
                              ? `Alta ${format(new Date(a.alta_familia), 'dd/MM/yy')}`
                              : 'Alta —'}
                            {' · '}
                            {a.ultimo_acceso
                              ? `último acceso ${format(new Date(a.ultimo_acceso), 'dd/MM/yy')} (${hace(a.ultimo_acceso)})`
                              : 'sin ningún acceso registrado'}
                            {a.email_confirmado ? ' · correo confirmado' : ''}
                            {meta && (
                              <span className={`${meta.clase} text-xs ml-2`} title={meta.title}>
                                {meta.label}
                              </span>
                            )}
                            {migrada && (
                              <span
                                className="block text-[11px] opacity-60"
                                title="Las migs 297/298/299 crearon la cuenta del tutor; el alta de arriba es la de la familia"
                              >
                                cuenta creada al convertir, el{' '}
                                {format(new Date(a.cuenta_creada), 'dd/MM/yy')}
                              </span>
                            )}
                          </p>
                        );
                      })()}

                    <div className="flex flex-wrap gap-2 mt-2">
                      {f.links.length === 0 ? (
                        <span className="text-text-secondary text-xs">Sin hijos vinculados</span>
                      ) : (
                        f.links.map((l) => (
                          <Link
                            key={l.id}
                            to={`/alumnos/${l.student_id}`}
                            className="badge-info text-xs hover:underline"
                            data-testid={`hijo-${l.id}`}
                          >
                            {l.student_name}
                            {l.relationship ? ` · ${l.relationship}` : ''}
                          </Link>
                        ))
                      )}
                    </div>
                  </div>

                  {/* BUG 14 · las dos acciones sobre la CUENTA del tutor, en la
                      única lista donde un tutor aparece. Solo si tiene cuenta:
                      un tutor declarativo no tiene nada que desactivar. */}
                  {f.hasAccount && (
                    <div className="flex shrink-0 gap-2">
                      {/* Reenviar el enlace de definir contraseña, como el
                          «Reinvitar» de /directores. Solo se ofrece a quien no
                          ha entrado o no puede: puesto siempre sería un botón
                          más que se deja de leer. */}
                      {(() => {
                        const estado = estadoDe(f, accesos.get(f.profile_id ?? ''));
                        if (
                          !f.email ||
                          (estado !== 'sin_confirmar' &&
                            estado !== 'sin_contrasena' &&
                            estado !== 'nunca')
                        ) {
                          return null;
                        }
                        return (
                          <button
                            type="button"
                            disabled={enviando}
                            className="btn-ghost text-xs whitespace-nowrap"
                            data-testid={`reenviar-acceso-${f.guardian_id}`}
                            onClick={() => setReenviando(f)}
                          >
                            <Send size={14} className="mr-1 inline" />
                            Reenviar acceso
                          </button>
                        );
                      })()}
                      <button
                        type="button"
                        disabled={busy}
                        className="btn-ghost text-xs whitespace-nowrap"
                        data-testid={`toggle-activa-${f.guardian_id}`}
                        onClick={() => setConfirmActiva(f)}
                      >
                        <Power size={14} className="mr-1 inline" />
                        {f.accountActive === false ? 'Activar' : 'Desactivar'}
                      </button>
                      {/* Borrar exige desactivada antes: lo pide el servidor
                          (22023), así que el botón no se ofrece hasta entonces
                          en vez de dejar que el error lo explique. */}
                      <button
                        type="button"
                        disabled={busy || f.accountActive !== false}
                        title={
                          f.accountActive !== false
                            ? 'Primero hay que desactivar la cuenta'
                            : 'Eliminar la cuenta definitivamente'
                        }
                        className="btn-ghost text-xs whitespace-nowrap text-error disabled:opacity-40"
                        data-testid={`eliminar-tutor-${f.guardian_id}`}
                        onClick={() => setEliminando(f)}
                      >
                        <Trash2 size={14} className="mr-1 inline" />
                        Eliminar
                      </button>
                    </div>
                  )}

                  {/* Los accesos retirados no se esconden en otra pantalla: son
                      la respuesta a "quién HA TENIDO acceso a este niño". */}
                  {f.revoked.length > 0 && (
                    <button
                      type="button"
                      className="btn-ghost text-xs whitespace-nowrap"
                      data-testid={`historial-${f.guardian_id}`}
                      onClick={() =>
                        setExpanded((e) => (e === f.guardian_id ? null : f.guardian_id))
                      }
                    >
                      {expanded === f.guardian_id ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                      {f.revoked.length} {f.revoked.length === 1 ? 'acceso' : 'accesos'} retirado
                      {f.revoked.length === 1 ? '' : 's'}
                    </button>
                  )}
                </div>

                {expanded === f.guardian_id && (
                  <ul
                    className="mt-3 pl-3 border-l border-bg-border space-y-1"
                    data-testid={`revocados-${f.guardian_id}`}
                  >
                    {f.revoked.map((l) => (
                      <li key={l.id} className="text-xs text-text-secondary">
                        <ShieldAlert size={12} className="inline mr-1 text-warning" />
                        <span className="text-white">{l.student_name}</span> — acceso desde el{' '}
                        {fecha(l.created_at)} hasta el {fecha(l.revoked_at)}
                        {l.revoked_reason ? ` · ${l.revoked_reason}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ---------- Confirmación de invitación ---------- */}
      <Modal
        open={inviting !== null}
        onClose={() => setInviting(null)}
        title="Dar acceso al tutor"
        size="sm"
      >
        {inviting && (
          <div className="space-y-4">
            {/* Nombrar a las dos personas no es cortesía: es lo único que impide
                conceder la ficha del niño equivocado desde una tabla de filas
                idénticas. */}
            <p className="text-text-secondary text-sm font-inter">
              <span className="text-white">{inviting.parent_name}</span> podrá ver la ficha completa
              de <span className="text-white">{inviting.student_name}</span>, incluidos sus datos
              médicos, su calendario y su conversación con el entrenador. También podrá apuntarle y
              desapuntarle de las sesiones.
            </p>
            <p className="text-text-secondary text-xs font-inter">
              Se le creará una cuenta con {inviting.parent_email} y recibirá un enlace para definir
              su contraseña.
            </p>
            <select
              className="input w-full text-sm"
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              data-testid="invitar-relationship"
            >
              {RELATIONSHIPS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-3">
              <button className="btn-ghost" onClick={() => setInviting(null)} disabled={busy}>
                Cancelar
              </button>
              <button
                className="btn-gold"
                onClick={handleInvite}
                disabled={busy}
                data-testid="confirmar-invitacion"
              >
                {busy ? 'Dando acceso…' : 'Dar acceso'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* BUG 14 · paso 1: desactivar/activar, con el patrón de MonitoresPage. */}
      <Confirm
        open={!!confirmActiva}
        onClose={() => setConfirmActiva(null)}
        onConfirm={() => confirmActiva && void toggleActiva(confirmActiva)}
        title={
          confirmActiva?.accountActive === false ? 'Activar cuenta' : 'Desactivar cuenta de tutor'
        }
        message={
          confirmActiva?.accountActive === false
            ? `¿Seguro que quieres activar la cuenta de ${confirmActiva?.full_name}? Volverá a poder entrar en la app.`
            : `¿Seguro que quieres desactivar la cuenta de ${confirmActiva?.full_name}? No podrá entrar y se le cerrarán las sesiones abiertas. Sus hijos y sus vínculos no se tocan.`
        }
        danger={confirmActiva?.accountActive !== false}
      />

      <Confirm
        open={!!reenviando}
        onClose={() => setReenviando(null)}
        onConfirm={() => reenviando && void reenviarAcceso(reenviando)}
        title="Reenviar el acceso"
        message={`Se enviará a ${reenviando?.email} un correo para que defina su contraseña. No se cambia nada de su cuenta ni de sus vínculos: si ya tenía contraseña, la de antes sigue valiendo hasta que use el enlace.`}
        confirmLabel={enviando ? 'Enviando…' : 'Enviar'}
      />

      {/* Paso 2: el borrado definitivo. Mismo modal que la ficha de un alumno
          —teclear el nombre, recuentos comprobados— más la nota del tutor. */}
      <EliminarAlumnoModal
        open={!!eliminando}
        cuenta={
          eliminando?.profile_id
            ? { id: eliminando.profile_id, full_name: eliminando.full_name }
            : null
        }
        tutor={
          eliminando
            ? {
                hijosVivos: eliminando.links.length,
                vinculosTotales: eliminando.links.length + eliminando.revoked.length,
              }
            : undefined
        }
        onClose={() => setEliminando(null)}
        onDeleted={() => eliminando && void tutorBorrado(eliminando)}
      />
    </div>
  );
}

function fecha(iso: string | null): string {
  if (!iso) return '—';
  return format(new Date(iso), 'dd/MM/yyyy');
}
